import { type ReadonlySignal, type Signal, signal } from '@preact/signals-core';

import type { RowSelectionHost, SelectionModifiers } from './types.js';

/**
 * View-agnostic multi-row selection. Reads row order from a host so
 * Shift+Click can fill the range between the anchor and the clicked
 * row. Views own when to `select()` or `clear()` — the controller
 * exposes the selection as signals only.
 */
export class RowSelectionController {
  private readonly _selected: Signal<ReadonlySet<string>> = signal(
    new Set<string>()
  );

  private readonly _lastClicked: Signal<string | null> = signal(null);

  readonly selectedIds$: ReadonlySignal<ReadonlySet<string>> = this._selected;

  readonly lastClickedId$: ReadonlySignal<string | null> = this._lastClicked;

  constructor(private readonly host: RowSelectionHost) {}

  has(rowId: string): boolean {
    return this._selected.value.has(rowId);
  }

  clear(): void {
    if (this._selected.value.size === 0 && this._lastClicked.value === null) {
      return;
    }
    this._selected.value = new Set();
    this._lastClicked.value = null;
  }

  /**
   * Atomically overwrite the internal selection from an outside source
   * (e.g. another store the view is mirroring). No-op when the incoming
   * ids match the current set — guards against cyclic sync effects.
   * The anchor is reset unless the caller passes one explicitly.
   */
  setSelected(ids: ReadonlySet<string>, anchor: string | null = null): void {
    const current = this._selected.value;
    if (
      current.size === ids.size &&
      [...ids].every(id => current.has(id)) &&
      this._lastClicked.value === anchor
    ) {
      return;
    }
    this._selected.value = new Set(ids);
    this._lastClicked.value = anchor;
  }

  select(rowId: string, mods: SelectionModifiers = {}, scope?: string): void {
    const { shift, ctrl, meta } = mods;
    if (shift) {
      this.applyShiftSelect(rowId, scope);
      return;
    }
    if (ctrl || meta) {
      this.applyToggle(rowId);
      return;
    }
    this._selected.value = new Set([rowId]);
    this._lastClicked.value = rowId;
  }

  private applyShiftSelect(rowId: string, scope?: string): void {
    const anchor = this._lastClicked.value;
    if (anchor !== null && anchor !== rowId) {
      const order = this.host.getRowIdsInOrder(scope);
      const from = order.indexOf(anchor);
      const to = order.indexOf(rowId);
      if (from !== -1 && to !== -1) {
        const [lo, hi] = from < to ? [from, to] : [to, from];
        const next = new Set(this._selected.value);
        for (let i = lo; i <= hi; i++) {
          const id = order[i];
          if (id !== undefined) next.add(id);
        }
        this._selected.value = next;
        this._lastClicked.value = rowId;
        return;
      }
    }
    this.applyToggle(rowId);
  }

  private applyToggle(rowId: string): void {
    const next = new Set(this._selected.value);
    if (next.has(rowId)) next.delete(rowId);
    else next.add(rowId);
    this._selected.value = next;
    this._lastClicked.value = rowId;
  }
}
